import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileInvoice,
  faFileInvoiceDollar,
  faBoxOpen,
  faCalendarAlt,
  faShoppingCart,
  faGlobe,
  faInfoCircle,
  faBuilding,
  faUser,
  faMoneyBillWave,
  faShippingFast,
  faSpinner,
  faExclamationTriangle,
  faFileCsv,
  faExpandAlt,
  faTimes,
  faChevronDown,
  faChevronUp,
  faListAlt,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

const InvoiceTab = ({ item }) => {
  const [invoiceData, setInvoiceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        if (!item?.header?.reference) {
          setLoading(false);
          setError("No reference available to fetch invoice data");
          return;
        }

        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/traffic`,
          {
            params: {
              reference: item.header.reference,
              responseType: "I",
            },
          }
        );

        setInvoiceData(Array.isArray(response.data) ? response.data : []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching invoice data:", error);
        setError("Failed to load invoice data. Please try again later.");
        setLoading(false);
      }
    };

    if (item?.header?.reference) {
      fetchInvoiceData();
    } else {
      setLoading(false);
    }
  }, [item]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatCurrency = (value, currency = "USD") => {
    if (value === undefined || value === null) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(value);
  };

  const toggleInvoiceExpansion = (invoiceNumber) => {
    if (expandedInvoice === invoiceNumber) {
      setExpandedInvoice(null);
    } else {
      setExpandedInvoice(invoiceNumber);
    }
  };

  const openInvoiceModal = (invoice) => {
    setSelectedInvoice(invoice);
    setIsModalOpen(true);
  };

  const closeInvoiceModal = () => {
    setIsModalOpen(false);
  };

  const exportToCSV = (invoice) => {
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent +=
      "Line Number,Part Number,Description,Fraction,Quantity,Origin,TLC,Prosec,IVA,Payment Method,Value,Weight\r\n";

    invoice.invoice_data.classification_data.forEach((row) => {
      let rowContent = `${row.line_number},${
        row.part_number
      },${row.part_description.replace(/,/g, "")},${row.fraction_number},${
        row.quantity
      },${row.country_origin},${row.tlc},${row.prosec},${row.tax_iva},${
        row.iva_payment_method
      },${row.line_value},${row.net_weight}`;
      csvContent += rowContent + "\r\n";
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `invoice_${invoice.invoice_data.invoice_number}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const FullScreenModal = ({ isOpen, onClose, invoice, onExportCsv }) => {
    if (!isOpen || !invoice) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
        <div className="min-h-screen flex items-center justify-center">
          <div className="bg-white p-4 max-w-7xl w-full max-h-full overflow-auto rounded-lg shadow-xl">
            {/* Modal Title Section */}
            <div className="flex items-center px-3 py-1 bg-blue-900 rounded-t-lg">
              <FontAwesomeIcon
                icon={faFileInvoice}
                className="text-blue-200 text-lg"
              />
              <h3 className="font-black text-lg ml-2 text-white">
                Invoice Details: {invoice.invoice_data.invoice_number}
              </h3>
              <div className="ml-auto flex items-center">
                <FontAwesomeIcon
                  icon={faFileCsv}
                  className="text-blue-200 text-lg cursor-pointer mr-2"
                  onClick={() => onExportCsv(invoice)}
                  title="Export to CSV"
                />
                <FontAwesomeIcon
                  icon={faTimes}
                  className="text-blue-200 text-lg cursor-pointer"
                  onClick={onClose}
                  title="Close"
                />
              </div>
            </div>

            <div className="p-4">
              {/* Invoice Header */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 border-b pb-1">
                    <FontAwesomeIcon
                      icon={faFileInvoiceDollar}
                      className="mr-2 text-blue-600"
                    />
                    Invoice Information
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-500">
                        Invoice Number:
                      </span>
                      <p className="text-sm font-medium">
                        {invoice.invoice_data.invoice_number}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Invoice Date:
                      </span>
                      <p className="text-sm font-medium">
                        {formatDate(invoice.invoice_data.invoice_date)}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">
                        Invoice Value:
                      </span>
                      <p className="text-sm font-medium">
                        {formatCurrency(
                          invoice.invoice_data.invoice_value,
                          invoice.invoice_data.currency
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 border-b pb-1">
                    <FontAwesomeIcon
                      icon={faBuilding}
                      className="mr-2 text-blue-600"
                    />
                    Supplier Information
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-500">Supplier:</span>
                      <p className="text-sm font-medium">
                        {invoice.invoice_data.supplier}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Country:</span>
                      <p className="text-sm font-medium">
                        {invoice.invoice_data.invoice_country}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Incoterm:</span>
                      <p className="text-sm font-medium">
                        {invoice.invoice_data.incoterm}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 border-b pb-1">
                    <FontAwesomeIcon
                      icon={faMoneyBillWave}
                      className="mr-2 text-blue-600"
                    />
                    Financial Information
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-500">
                        Cargo Value:
                      </span>
                      <p className="text-sm font-medium">
                        {formatCurrency(
                          invoice.invoice_data.cargo_value,
                          invoice.invoice_data.currency
                        )}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Currency:</span>
                      <p className="text-sm font-medium">
                        {invoice.invoice_data.currency}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Cargo Type:</span>
                      <p className="text-sm font-medium">
                        {invoice.invoice_data.cargo || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice Items Table */}
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 border-b pb-2 flex items-center">
                  <FontAwesomeIcon
                    icon={faBoxOpen}
                    className="mr-2 text-blue-600"
                  />
                  Invoice Line Items
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Line
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Part Number
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Description
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Fraction
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Quantity
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Origin
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          TLC
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Prosec
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          IVA
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Value
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Weight
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoice.invoice_data.classification_data.map(
                        (item, index) => (
                          <tr
                            key={index}
                            className={
                              index % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.line_number}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.part_number}
                            </td>
                            <td
                              className="px-3 py-2 text-sm text-gray-900 max-w-xs truncate"
                              title={item.part_description}
                            >
                              {item.part_description}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.fraction_number}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.quantity.toLocaleString()}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.country_origin}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.tlc}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.prosec}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.tax_iva}%
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(
                                item.line_value,
                                invoice.invoice_data.currency
                              )}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.net_weight
                                ? `${item.net_weight.toLocaleString()} kg`
                                : "N/A"}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-blue-500 text-4xl animate-spin mb-4"
        />
        <p className="text-gray-600">Loading invoice data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col items-center justify-center">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-yellow-500 text-4xl mb-4"
          />
          <p className="text-gray-700 mb-2 font-medium">{error}</p>
          <p className="text-gray-500 text-sm">
            Please check the reference number and try again.
          </p>
        </div>
      </div>
    );
  }

  if (invoiceData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col items-center justify-center">
          <FontAwesomeIcon
            icon={faFileInvoice}
            className="text-gray-400 text-4xl mb-4"
          />
          <p className="text-gray-700 mb-2 font-medium">
            No invoice data available
          </p>
          <p className="text-gray-500 text-sm">
            There are no invoices associated with this shipment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Invoice Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
          <FontAwesomeIcon
            icon={faFileInvoiceDollar}
            className="mr-2 text-blue-600"
          />
          Invoice Summary
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-xs text-gray-500">Number of Invoices:</span>
            <p className="font-medium">{invoiceData.length}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Total Value:</span>
            <p className="font-medium">
              {formatCurrency(
                invoiceData.reduce(
                  (sum, inv) => sum + (inv.invoice_data.invoice_value || 0),
                  0
                ),
                invoiceData[0]?.invoice_data?.currency
              )}
            </p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Currency:</span>
            <p className="font-medium">
              {invoiceData[0]?.invoice_data?.currency || "N/A"}
            </p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Incoterm:</span>
            <p className="font-medium">
              {invoiceData[0]?.invoice_data?.incoterm || "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Invoice List */}
      {invoiceData.map((invoice, index) => (
        <div
          key={index}
          className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
        >
          {/* Invoice Header - Clickable */}
          <div
            className={`flex justify-between items-center px-4 py-3 cursor-pointer ${
              expandedInvoice === invoice.invoice_data.invoice_number
                ? "bg-blue-50"
                : "hover:bg-gray-50"
            }`}
            onClick={() =>
              toggleInvoiceExpansion(invoice.invoice_data.invoice_number)
            }
          >
            <div className="flex items-center">
              <FontAwesomeIcon
                icon={faFileInvoice}
                className="text-blue-600 mr-3"
              />
              <div>
                <h3 className="font-medium text-gray-900">
                  Invoice #{invoice.invoice_data.invoice_number}
                </h3>
                <p className="text-xs text-gray-500">
                  {formatDate(invoice.invoice_data.invoice_date)} | Supplier:{" "}
                  {invoice.invoice_data.supplier}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium">
                {formatCurrency(
                  invoice.invoice_data.invoice_value,
                  invoice.invoice_data.currency
                )}
              </span>
              <FontAwesomeIcon
                icon={
                  expandedInvoice === invoice.invoice_data.invoice_number
                    ? faChevronUp
                    : faChevronDown
                }
                className="text-gray-500"
              />
            </div>
          </div>

          {/* Expanded Invoice Details */}
          {expandedInvoice === invoice.invoice_data.invoice_number && (
            <div className="border-t border-gray-200 p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Invoice Info */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase">
                    Invoice Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">
                        Invoice Date:
                      </span>
                      <span className="text-sm">
                        {formatDate(invoice.invoice_data.invoice_date)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">
                        Invoice Value:
                      </span>
                      <span className="text-sm">
                        {formatCurrency(
                          invoice.invoice_data.invoice_value,
                          invoice.invoice_data.currency
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Currency:</span>
                      <span className="text-sm">
                        {invoice.invoice_data.currency}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Supplier Info */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase">
                    Supplier Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Supplier:</span>
                      <span className="text-sm">
                        {invoice.invoice_data.supplier}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Country:</span>
                      <span className="text-sm">
                        {invoice.invoice_data.invoice_country}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Incoterm:</span>
                      <span className="text-sm">
                        {invoice.invoice_data.incoterm}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Cargo Info */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase">
                    Cargo Information
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">
                        Cargo Value:
                      </span>
                      <span className="text-sm">
                        {formatCurrency(
                          invoice.invoice_data.cargo_value,
                          invoice.invoice_data.currency
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Cargo Type:</span>
                      <span className="text-sm">
                        {invoice.invoice_data.cargo || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Line Items:</span>
                      <span className="text-sm">
                        {invoice.invoice_data.classification_data.length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice Items Preview - Show only first 3 items */}
              <div className="mt-4">
                <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase">
                  Line Items Preview
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Line
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Part Number
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Description
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Fraction
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Quantity
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoice.invoice_data.classification_data
                        .slice(0, 3) // Show only first 3 items
                        .map((item, idx) => (
                          <tr
                            key={idx}
                            className={
                              idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.line_number}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.part_number}
                            </td>
                            <td
                              className="px-3 py-2 text-sm text-gray-900 max-w-xs truncate"
                              title={item.part_description}
                            >
                              {item.part_description}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.fraction_number}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {item.quantity.toLocaleString()}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(
                                item.line_value,
                                invoice.invoice_data.currency
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Show more button if there are more than 3 items */}
                {invoice.invoice_data.classification_data.length > 3 && (
                  <div className="mt-3 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openInvoiceModal(invoice);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Show all {invoice.invoice_data.classification_data.length}{" "}
                      items...
                    </button>
                  </div>
                )}
              </div>

              {/* Export Button */}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    exportToCSV(invoice);
                  }}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
                  Export to CSV
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* New Addition: Invoice Detail Section below invoice list */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3 border-b pb-2 flex items-center">
          <FontAwesomeIcon icon={faListAlt} className="mr-2 text-blue-600" />
          All Invoice Details
        </h3>

        {/* Combined invoice items table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Invoice #
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Line
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Part Number
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Description
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Fraction
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Quantity
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Origin
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Value
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoiceData.flatMap((invoice, invoiceIndex) =>
                invoice.invoice_data.classification_data.map(
                  (item, itemIndex) => (
                    <tr
                      key={`${invoiceIndex}-${itemIndex}`}
                      className={
                        (invoiceIndex + itemIndex) % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50"
                      }
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {invoice.invoice_data.invoice_number}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {item.line_number}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {item.part_number}
                      </td>
                      <td
                        className="px-3 py-2 text-sm text-gray-900 max-w-xs truncate"
                        title={item.part_description}
                      >
                        {item.part_description}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {item.fraction_number}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {item.quantity.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {item.country_origin}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(
                          item.line_value,
                          invoice.invoice_data.currency
                        )}
                      </td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>

        {/* Export All Button */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => {
              // Export all invoices data
              const allInvoicesData = invoiceData.map((invoice) => ({
                ...invoice,
                filename: `all_invoices_${
                  item?.header?.reference || "export"
                }.csv`,
              }));

              // Create single CSV with all invoice data
              let csvContent = "data:text/csv;charset=utf-8,";
              csvContent +=
                "Invoice Number,Line Number,Part Number,Description,Fraction,Quantity,Origin,TLC,Prosec,IVA,Payment Method,Value,Weight\r\n";

              allInvoicesData.forEach((invoice) => {
                invoice.invoice_data.classification_data.forEach((row) => {
                  let rowContent = `${invoice.invoice_data.invoice_number},${
                    row.line_number
                  },${row.part_number},${row.part_description.replace(
                    /,/g,
                    ""
                  )},${row.fraction_number},${row.quantity},${
                    row.country_origin
                  },${row.tlc},${row.prosec},${row.tax_iva},${
                    row.iva_payment_method
                  },${row.line_value},${row.net_weight}`;
                  csvContent += rowContent + "\r\n";
                });
              });

              const encodedUri = encodeURI(csvContent);
              const link = document.createElement("a");
              link.setAttribute("href", encodedUri);
              link.setAttribute(
                "download",
                `all_invoices_${item?.header?.reference || "export"}.csv`
              );
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
            Export All to CSV
          </button>
        </div>
      </div>

      {/* Invoice Detail Modal */}
      <FullScreenModal
        isOpen={isModalOpen}
        onClose={closeInvoiceModal}
        invoice={selectedInvoice}
        onExportCsv={exportToCSV}
      />
    </div>
  );
};

export default InvoiceTab;
